const DISMISS_KEY = "wt-announcement-dismissed";
const SLIDE_INTERVAL = 5000;
const SLIDE_TRANSITION = "transform 0.5s ease-in-out";
const SLIDE_DURATION = 500;
const MOBILE_BREAKPOINT = 900;
const SWIPE_THRESHOLD = 30;

class AnnouncementBar extends HTMLElement {
  static isDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementInterval = null;
    this.resetTimeout = null;
    this.loopClone = null;
    this.slideCount = 0;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.clearAutoplay = this.clearAutoplay.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
  }

  connectedCallback() {
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.slideCount = this.getSlideCount();

    if (!AnnouncementBar.isDismissed() && this.slideCount > 0) {
      this.style.display = "block";
    }

    const isMarquee = this.announcementBar?.classList.contains(
      "wt-announcement__container--marquee",
    );

    if (!isMarquee && this.announcementBar) {
      this.setupLoopClone();

      if (this.canAutoplay()) {
        this.announcementInterval = setInterval(
          this.changeAnnouncement,
          SLIDE_INTERVAL,
        );
      }

      this.announcementBar.addEventListener(
        "touchstart",
        this.handleTouchStart,
        { passive: true },
      );
      this.announcementBar.addEventListener("touchmove", this.handleTouchMove, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchstart", this.clearAutoplay, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchend", this.restartInterval, {
        passive: true,
      });

      this.announcementBar.style.transition = SLIDE_TRANSITION;
    }

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton) this.closeButton.addEventListener("click", this.dismiss);

    window.addEventListener("resize", this.handleResize);
  }

  getSlideCount() {
    const declared = Number(this.announcementBar?.dataset.announcementsCount);
    if (Number.isFinite(declared) && declared > 0) return declared;
    return this.announcementBar?.children.length || 0;
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  canAutoplay() {
    return this.slideCount > 1 && !this.prefersReducedMotion();
  }

  setupLoopClone() {
    if (this.loopClone || this.slideCount < 2) return;

    const first = this.announcementBar.querySelector(".wt-announcement__body");
    if (!first) return;

    const clone = first.cloneNode(true);
    clone.classList.add("wt-announcement__clone");
    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("role");
    clone.removeAttribute("aria-label");
    // Never let the theme editor treat the clone as a real block.
    Array.from(clone.attributes).forEach((attr) => {
      if (attr.name.startsWith("data-shopify")) clone.removeAttribute(attr.name);
    });
    clone.querySelectorAll("a").forEach((link) => {
      link.setAttribute("tabindex", "-1");
      link.setAttribute("aria-hidden", "true");
    });

    this.announcementBar.appendChild(clone);
    this.loopClone = clone;
  }

  handleResize() {
    if (window.innerWidth >= MOBILE_BREAKPOINT && this.announcementBar) {
      clearTimeout(this.resetTimeout);
      this.announcementBar.style.transition = "none";
      this.announcementBar.style.transform = "translateX(0)";
      this.currentIndex = 0;
      this.restoreTransition();
    }
  }

  isMobileView() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  clearAutoplay() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = null;
  }

  restartInterval() {
    this.clearAutoplay();
    if (this.canAutoplay()) {
      this.announcementInterval = setInterval(
        this.changeAnnouncement,
        SLIDE_INTERVAL,
      );
    }
  }

  dismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
    }
    this.clearAutoplay();
    this.style.display = "none";
  }

  restoreTransition() {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (this.announcementBar)
          this.announcementBar.style.transition = SLIDE_TRANSITION;
      }),
    );
  }

  snapToStart() {
    if (!this.announcementBar) return;

    clearTimeout(this.resetTimeout);
    this.currentIndex = 0;
    this.announcementBar.style.transition = "none";
    this.announcementBar.style.transform = "translateX(0)";
    this.restoreTransition();
  }

  goToSlide(index) {
    if (!this.announcementBar) return;

    this.currentIndex = index;
    this.announcementBar.style.transition = SLIDE_TRANSITION;
    this.announcementBar.style.transform = `translateX(${-(index * 100)}vw)`;

    if (this.loopClone && index === this.slideCount) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = setTimeout(() => this.snapToStart(), SLIDE_DURATION);
    }
  }

  changeAnnouncement() {
    if (!this.isMobileView() || !this.announcementBar || this.slideCount < 2) {
      return;
    }

    if (!this.loopClone) {
      this.goToSlide((this.currentIndex + 1) % this.slideCount);
      return;
    }

    if (this.currentIndex >= this.slideCount) {
      this.snapToStart();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.goToSlide(1)),
      );
      return;
    }

    this.goToSlide(this.currentIndex + 1);
  }

  previousAnnouncement() {
    if (!this.isMobileView() || !this.announcementBar || this.slideCount < 2) {
      return;
    }

    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
      return;
    }

    if (!this.loopClone) {
      this.goToSlide(this.slideCount - 1);
      return;
    }

    clearTimeout(this.resetTimeout);
    this.announcementBar.style.transition = "none";
    this.announcementBar.style.transform = `translateX(${-(this.slideCount * 100)}vw)`;
    this.currentIndex = this.slideCount;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.goToSlide(this.slideCount - 1)),
    );
  }

  handleTouchStart(e) {
    if (this.isMobileView()) {
      this.touchStartX = e.touches[0].clientX;
      this.touchEndX = this.touchStartX;
    }
  }

  handleTouchMove(e) {
    if (this.isMobileView()) {
      this.touchEndX = e.touches[0].clientX;
    }
  }

  handleTouchEnd() {
    if (Math.abs(this.touchEndX - this.touchStartX) > SWIPE_THRESHOLD) {
      if (this.touchEndX < this.touchStartX) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
  }

  disconnectedCallback() {
    this.clearAutoplay();
    clearTimeout(this.resetTimeout);

    this.announcementBar?.removeEventListener(
      "touchstart",
      this.handleTouchStart,
    );
    this.announcementBar?.removeEventListener("touchmove", this.handleTouchMove);
    this.announcementBar?.removeEventListener("touchend", this.handleTouchEnd);
    this.announcementBar?.removeEventListener("touchstart", this.clearAutoplay);
    this.announcementBar?.removeEventListener("touchend", this.restartInterval);
    this.closeButton?.removeEventListener("click", this.dismiss);
    window.removeEventListener("resize", this.handleResize);
  }
}

customElements.define("announcement-bar", AnnouncementBar);
